import React, { useEffect, useState } from 'react';

import { Box, Button, Typography, Stack } from '@mui/material';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as ChevronLeftWhite } from 'src/common/assets/icons/chevron_left_white.svg';
import { ReactComponent as CloudIcon } from 'src/common/assets/icons/cloud_icon.svg';
import { FlorencePAGES } from 'src/common/components/tfa-auth/constants/pages';
import { useAppSelector } from 'src/common/hooks/reduxHooks';
import BatchUploadContainer from 'src/florence/components/BatchUploadContainer/BatchUploadContainer';
import MobileSubscriptionPopup from 'src/florence/components/SubscriptionPopups/MobileSubscriptionPopup';
import NoSubscriptionPopup from 'src/florence/components/SubscriptionPopups/NoSubscriptionPopup';
import { useFileDrop } from 'src/florence/hooks/useFileDrop';

type Props = {
  clinicNotes: boolean;
  onClose: () => void;
};

const UploadDocuments: React.FC<Props> = ({ clinicNotes, onClose }) => {
  const { user } = useAppSelector((state) => state.model);

  const [noSubscriptionPopup, setNoSubscriptionPopup] = useState(false);
  const [mobileSubscriptionPopup, setMobileSubscriptionPopup] = useState(false);
  const processedOcrs = useAppSelector((state) => state.model.processedOcrs);

  const navigate = useNavigate();

  const uploadingOcrsLength = React.useMemo(() => {
    return processedOcrs.filter((o) => o.isUploading).length;
  }, [processedOcrs]);

  const completedOcrsLength = React.useMemo(() => {
    return processedOcrs.filter((o) => o.uploadComplete).length;
  }, [processedOcrs]);

  const dropDisabled = React.useMemo(() => {
    if (_.isEmpty(processedOcrs)) return false;
    return (
      uploadingOcrsLength + completedOcrsLength === processedOcrs.length ||
      uploadingOcrsLength > 0
    );
  }, [processedOcrs, uploadingOcrsLength, completedOcrsLength]);
  const { error, getRootProps, getInputProps } = useFileDrop(dropDisabled);

  useEffect(() => {
    if (!user || user.organizationId) return;

    if (!user.activeSubscriptionType) {
      setNoSubscriptionPopup(true);
    } else if (user.activeSubscriptionType === 'mobile') {
      setMobileSubscriptionPopup(true);
    } else if (user.activeSubscriptionType === 'trial') {
      if (user.trialScansAvailable === 0 && user.purchasedScansAvailable === 0)
        navigate(`${FlorencePAGES.scanPackage}`);
    } else if (user.purchasedScansAvailable === 0) {
      navigate(`${FlorencePAGES.scanPackage}`);
    }
  }, [user, navigate]);

  return (
    <Box>
      <Box>
        <Button
          disabled={uploadingOcrsLength > 0}
          variant='text'
          onClick={() => onClose()}
          sx={{ marginBottom: '10px' }}
        >
          <ChevronLeftWhite />
          <Typography color='white'>Back</Typography>
        </Button>
      </Box>

      <Box>
        <Typography color='text.primary' fontSize='32px' fontWeight={700}>
          Upload {clinicNotes ? 'Clinic' : 'Op'} Notes
        </Typography>
      </Box>

      <Box
        sx={{
          marginTop: '24px',
        }}
      >
        <Stack spacing={2.5}>
          <Box
            {...getRootProps()}
            sx={{
              opacity: dropDisabled ? 0.4 : 1,
              display: 'flex',
              backgroundColor: 'rgba(255, 255, 255, 0.20)',
              minHeight: '105px',
              flexDirection: 'row',
              flex: 1,
              borderRadius: '24px',
              border: '2px dashed #ffffff',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '22px',
              flexGrow: 0,
            }}
          >
            <Box>
              <Box
                sx={{
                  flexDirection: 'row',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <CloudIcon style={{ marginRight: '10px' }} />
                <Typography>Drag and drop files here to scan</Typography>
                <Button variant='text' disabled={dropDisabled}>
                  <input {...getInputProps()} />
                  <Typography color='white'>or browse files</Typography>
                </Button>
              </Box>
              <Typography
                color='text.secondary'
                variant='subtitle2'
                textAlign='center'
              >
                (pdf, doc, docx, png, jpeg, tiff, txt, or odt)
              </Typography>
            </Box>
          </Box>
          <BatchUploadContainer clinicNotes={clinicNotes} />
          {error && <Typography color='error'>{error}</Typography>}
        </Stack>
      </Box>

      {user && (
        <>
          <MobileSubscriptionPopup
            user={user}
            open={mobileSubscriptionPopup}
            onConfirm={() => {
              setMobileSubscriptionPopup(false);
              onClose();
            }}
            onClose={() => {
              setMobileSubscriptionPopup(false);
              onClose();
            }}
          />
          <NoSubscriptionPopup
            open={noSubscriptionPopup}
            onConfirm={() => {
              setMobileSubscriptionPopup(false);
              onClose();
            }}
            onClose={() => {
              setNoSubscriptionPopup(false);
              onClose();
            }}
          />
        </>
      )}
    </Box>
  );
};

export default UploadDocuments;
